import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  blogFetch,
  blogSaveArticle,
  blogSaveCategory,
  blogPublishArticle,
  blogUnpublishArticle,
  blogDeleteArticle,
  blogReorderArticles,
  blogUploadMedia,
  blogGenerateQuiz,
  blogStartArticleJob,
  blogStartArticleGeneration,
  blogPollArticleJobs,
  selectBlogArticleJob,
  selectBlogArticleBusy,
  selectBlogArticleJobError,
  pollWhileVisible,
  blogFetchTenantStatus,
  selectBlogTenantStatus,
  selectBlogTenantStatusError,
  selectBlogTenantStatusLoading,
  blogSaveErrorMessage,
  blogNeededCredits,
  blogInsufficientCredits,
  type BlogWritePart,
} from '@/store/slices/blog';
import { creditCostFor, fetchBlogCreditsPricing, type BlogCreditActionKey } from '@/store/slices/aiCredits';
import { fetchOwnerWallets } from '@/store/slices/wallet';
import { BuyCreditsSheet } from '../creatives/components/BuyCreditsSheet';
import {
  PREFERENCE_KEYS,
  saveUserPreferences,
  sameBlogAuthorPrefs,
  selectBlogAuthorPref,
  selectOwnerProfilePrefs,
} from '@/store/slices/userPreferences';
import { PageShell } from '../../components/PageShell';
import { Panel } from '../../components/Panel';
import { SectionTitle } from '../../components/SectionTitle';
import { BackHeader } from '../../components/BackHeader';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { ActionMenu, type ActionMenuItem } from '../../components/ActionMenu';
import { Field } from '../../components/Field';
import { Select, type SelectOption } from '../../components/Select';
import { TextArea } from '../../components/TextArea';
import { Chip } from '../../components/Chip';
import { Skeleton } from '../../components/Skeleton';
import { EmptyState } from '../../components/EmptyState';
import { Modal } from '../../components/Modal';
import { Toast } from '../../components/Toast/Toast';
import { useToast } from '../../hooks/useToast';
import type { BlogArticle, BlogArticleDraft } from '../../mock-data/types';
import { ROUTE } from '../../routes';
import { AFFILIATE_FORM_ANIM, ANIM } from '../../theme/tokens';
import { slugify } from '../blog/slug';
import { removeNewArticle } from '../blog/newArticles';
import { profileByline } from '../blog/byline';
import { articleLanguageCodes } from './languageOptions';
import { languageName } from '../../config/languageEndonyms';
import { toAppLang } from '../../config/langCodeMap';
import { MarkdownEditor, type QuizDraft } from './components/MarkdownEditor';
import { HeroField } from './components/HeroField';
import { ArticleSettingsSheet } from './components/ArticleSettingsSheet';
import { PreviewSheet } from './components/PreviewSheet';
import type { ReadingOrderItem } from './components/ReadingOrderList';
import { StringListEditor, FaqEditor, SourcesEditor, TagInput } from './components/SeoLists';
import { AuthorField } from './components/AuthorField';
import { GenerateButton } from './components/GenerateButton';
import { GeneratingWash } from './components/GeneratingWash';
import { BriefModal } from './components/BriefModal';
import { CategoryPrompt, type CategorySelection } from './components/CategoryPrompt';
import {
  Section,
  PanelBody,
  PanelHead,
  WashHost,
  SplitRow,
  StatusRow,
  HeaderActions,
  SlugCaption,
  ErrorHint,
  FooterBar,
  FooterSave,
  FooterPublish,
  WarnList,
  WarnItem,
  ModalCopy,
  LoadingStack,
  ChipRow,
  Group,
  GroupLabel,
  RetryWrap,
} from './blog-editor.styles';

type ConfirmKind = 'publish' | 'unpublish' | 'delete' | 'generate';

/**
 * Which action stopped at the "assign a category" dialog, so that finishing the
 * dialog finishes that action: a save goes out with the category, a publish
 * moves on to its own confirm.
 */
type CategoryIntent = 'save' | 'publish';

/**
 * One opening of the "assign a category" dialog. A new object per opening, so
 * that a continuation started by one opening can tell, after its round-trip,
 * whether the owner closed it and opened another meanwhile.
 */
type CategoryPromptSession = { intent: CategoryIntent };

/**
 * Which Generate button is running. One at a time, deliberately: they overlap on
 * the same fields (an article writes the body the SEO pass would then be reading
 * a stale copy of), and a page with two of them going has no honest way to show
 * where the wait is.
 */
type Generation = 'article' | 'content' | 'seo' | 'hero';

/**
 * The service names the same four passes slightly differently — its job `kind`
 * is what the editor calls a Generation, except that a body pass is `body`
 * there and `content` here. Mapped rather than renamed: `kind` is the wire
 * format, and the editor's word is what its own sections are keyed by.
 */
const GENERATION_FOR_JOB_KIND: Record<string, Generation> = {
  article: 'article',
  body: 'content',
  seo: 'seo',
  hero: 'hero',
};

/** What each generation asks the service for. */
const PARTS: Record<Generation, BlogWritePart[]> = {
  article: ['article'],
  content: ['body'],
  seo: ['seo'],
  hero: ['hero'],
};

/**
 * The editor's word for a pass → the action it is priced as in spark-blog's
 * credit price table. Same mapping as PARTS, in the pricing vocabulary; `quiz`
 * has no Generation (it is synchronous) but is priced all the same.
 */
const CREDIT_ACTION_FOR: Record<Generation | 'quiz', BlogCreditActionKey> = {
  article: 'article',
  content: 'body',
  seo: 'seo',
  hero: 'hero',
  quiz: 'quiz',
};

/** The line shown over the page while a whole article is being written. */
const PAGE_CAPTION = 'Lumio is writing your article. This takes a few minutes.';

/**
 * Sentinel option value for "start a new translation group". A real UUID is
 * minted on selection rather than up front, so an owner who opens the picker
 * and changes their mind does not leave the article carrying a group of one.
 */
const NEW_TRANSLATION_GROUP = '__new__';

const emptyDraft = (): BlogArticleDraft => ({
  slug: '',
  title: '',
  description: '',
  language: 'en',
  categoryId: '',
  pillar: false,
  // The wl scaffolder's default: new spokes sort after the curated openers.
  sequence: 10,
  bodyMarkdown: '',
  keyTakeaways: [],
  faq: [],
  sources: [],
  tags: [],
  entities: [],
  relatedSlugs: [],
});

/**
 * The article editor (`/blog/new` + `/blog/:id/edit`) — step 3-4 of
 * `docs/blog-authoring-plan.md`. One form over `BlogArticleDraft`: article
 * details (title, slug, category, language, hero), the TipTap markdown body, and
 * the whitelabels SEO field set. Placement inside the category (pillar + reading
 * order) lives behind the header's gear, and the article as a reader sees it
 * behind the eye beside it.
 *
 * Saves are drafts until the explicit Publish confirm; published articles keep
 * editing in place (dateModified bumps) with Unpublish behind its own confirm.
 * Both Save and Publish stop on an article with no category, at a dialog that
 * assigns one (existing or made on the spot) and then carries on. The saves a
 * generation makes before running are not gated: the service creates
 * categoryless drafts itself, and the invariant here is that the OWNER never
 * saves or publishes one.
 * Publish, Unpublish and Delete all hang off the kebab next to the status chip.
 * The footer, pinned so a long form never hides it, carries Save and, for a
 * saved draft, Publish as well (the same handler as the kebab's).
 */
export const PageBlogEditor = (): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { toast, show } = useToast();
  const { id } = useParams<{ id: string }>();

  const loaded = useAppSelector((state) => state.blog.loaded);
  const loading = useAppSelector((state) => state.blog.loading);
  const fetchError = useAppSelector((state) => state.blog.error);
  const saving = useAppSelector((state) => state.blog.saving);
  const categories = useAppSelector((state) => state.blog.categories);
  const articles = useAppSelector((state) => state.blog.articles);
  /** The byline the owner last saved an article under, or null the first time. */
  const rememberedAuthor = useAppSelector(selectBlogAuthorPref);
  /** The owner's profile: its name is the byline of last resort for a new article. */
  const ownerProfile = useAppSelector(selectOwnerProfilePrefs);
  /** Until the preferences load, both of the above are unknown rather than absent. */
  const prefsLoaded = useAppSelector((state) => state.userPreferences.loaded);

  /* ── AI credits: cost on the buttons + the buy-credits fallback ──────────
   *
   * The same two-layer contract as creatives: the per-action price table puts
   * the cost ON each Generate control and backs a fail-open pre-flight gate
   * (unknown balance or unknown price blocks nothing), and the service's 402
   * is the authoritative refusal — it opens the Buy-credits sheet primed with
   * the shortfall rather than dead-ending in a toast. Buttons are never
   * disabled for affordability; an unaffordable click is how the owner finds
   * the top-up.
   */
  const blogPricing = useAppSelector((state) => state.aiCredits.blogPricing);
  const creditBalance = useAppSelector((state) => state.ownerWallet.credits);

  React.useEffect(() => {
    void dispatch(fetchBlogCreditsPricing());
  }, [dispatch]);

  // Opening the article is what makes it "seen": its "New" badge on the list
  // (the Autopilot notice the owner followed from Home) goes with it. Keyed on
  // the route param and the brand the list wrote it under.
  const brandId = useAppSelector((state) => state.casinoInstance.activeBrand?.id);
  React.useEffect(() => {
    if (id && brandId) removeNewArticle(brandId, id);
  }, [id, brandId]);

  const [buyOpen, setBuyOpen] = React.useState(false);
  // The shortfall a 402 reported, or undefined when opened from the pre-flight
  // gate (the sheet then defaults to its money-anchored suggestions).
  const [buySuggested, setBuySuggested] = React.useState<number | undefined>(undefined);
  const openBuy = React.useCallback((suggested?: number) => {
    setBuySuggested(suggested && suggested > 0 ? suggested : undefined);
    setBuyOpen(true);
  }, []);

  const costFor = React.useCallback(
    (action: Generation | 'quiz'): number | undefined => creditCostFor(blogPricing, CREDIT_ACTION_FOR[action]),
    [blogPricing],
  );
  const afford = React.useCallback(
    (action: Generation | 'quiz'): boolean => {
      const cost = costFor(action);
      if (creditBalance == null || cost == null) return true;
      return creditBalance >= cost;
    },
    [costFor, creditBalance],
  );

  React.useEffect(() => {
    // The error guard stops the retry loop a rejected fetch would otherwise
    // cause (loading flips back to false with loaded still false).
    if (!loaded && !loading && fetchError == null) void dispatch(blogFetch());
  }, [dispatch, loaded, loading, fetchError]);

  /*
   * Whether this brand HAS a blog, asked the same way the list asks it.
   *
   * The editor used not to ask at all: the list owns the on/off switch, so the
   * check lived only there — and this page is reachable without passing
   * through it (a deep link, a back/forward, or a brand switch under an open
   * editor). An author then wrote a whole article into a brand with no tenant
   * row and met "Tenant not found" on the first save, which names something
   * they have never heard of and offers nothing to do about it.
   */
  const tenantStatus = useAppSelector(selectBlogTenantStatus);
  const tenantStatusLoading = useAppSelector(selectBlogTenantStatusLoading);
  const tenantStatusError = useAppSelector(selectBlogTenantStatusError);

  React.useEffect(() => {
    if (tenantStatus === null && !tenantStatusLoading && tenantStatusError == null) {
      void dispatch(blogFetchTenantStatus());
    }
  }, [dispatch, tenantStatus, tenantStatusLoading, tenantStatusError]);

  /*
   * The brand this page was opened for. A brand switch sends the author back
   * to the list.
   *
   * An article id means nothing under another brand — the blog is per-tenant
   * and the tenant IS the active brand — and an unsaved draft was written for
   * the brand that is no longer selected. The store already drops the previous
   * brand's articles on a switch, but that leaves THIS page mounted over a
   * draft it can no longer save: the write goes to the new brand's tenant,
   * which may not even have a blog.
   *
   * The first defined id is not a switch: the brand resolves from undefined on
   * boot, and treating that as one would bounce anyone deep-linking here.
   */
  const activeBrandId = useAppSelector((state) => state.casinoInstance.activeBrand?.id);
  const openedForBrand = React.useRef<string | undefined>(activeBrandId);

  React.useEffect(() => {
    if (activeBrandId === undefined) return;

    if (openedForBrand.current === undefined) {
      openedForBrand.current = activeBrandId;

      return;
    }

    if (openedForBrand.current !== activeBrandId) navigate(ROUTE.blog());
  }, [activeBrandId, navigate]);

  const [draft, setDraft] = React.useState<BlogArticleDraft | null>(id ? null : emptyDraft());

  // Resolved through the draft's id as well as the URL's: saving a NEW article
  // swaps the canonical URL with `history.replaceState` (see saveDraft), which
  // React Router never observes, so `useParams().id` stays undefined for the
  // rest of the session. Keying on the param alone left a just-published
  // article rendering as a draft — 'Draft' badge, Publish offered again, and
  // 'Delete draft' on a live article.
  const articleId = id ?? draft?.id;
  const article = articleId ? articles.find((a) => a.id === articleId) : undefined;

  /*
   * What is being written into THIS article, as the service reports it.
   *
   * The editor used to hold this in its own state, which meant it was true for
   * exactly one browser tab: a refresh forgot it, a second tab offered Generate
   * on an article already being written, and the one-at-a-time rule was a ref
   * that only ever guarded the page it lived on. Reading it from the service is
   * what makes the buttons go inert for EVERYONE — including the owner who
   * started nothing and is simply looking at the article.
   */
  const job = useAppSelector(selectBlogArticleJob(articleId));
  const jobError = useAppSelector(selectBlogArticleJobError(articleId));
  const busyHere = useAppSelector(selectBlogArticleBusy(articleId));
  const generating: Generation | null =
    busyHere && job ? (GENERATION_FOR_JOB_KIND[job.kind] ?? 'article') : null;
  const [showErrors, setShowErrors] = React.useState(false);
  const [confirm, setConfirm] = React.useState<ConfirmKind>('publish');
  /**
   * The "assign a category" dialog, keyed by the action that opened it; null
   * while closed. Save and Publish both stop here on an article with no
   * category, and both carry on by themselves once it has one.
   */
  const [categoryPrompt, setCategoryPrompt] = React.useState<CategoryPromptSession | null>(null);
  /**
   * The prompt as it is NOW, for the continuation that runs after a category
   * has been created: that await spans a network round-trip, and a dialog the
   * owner closed meanwhile (or closed and reopened) must not save or publish
   * when the answer lands.
   */
  const categoryPromptRef = React.useRef<CategoryPromptSession | null>(null);
  categoryPromptRef.current = categoryPrompt;
  /**
   * The last action the prompt was opened for, so its button keeps that label
   * through the exit animation: the session is cleared on close, and the dialog
   * is still on screen for a moment after.
   */
  const lastCategoryIntent = React.useRef<CategoryIntent>('save');
  if (categoryPrompt) lastCategoryIntent.current = categoryPrompt.intent;
  /** The publish confirm waits for the prompt to leave; see `assignCategory`. */
  const confirmAfterPrompt = React.useRef<number | undefined>(undefined);
  React.useEffect(() => () => window.clearTimeout(confirmAfterPrompt.current), []);
  /**
   * The generation waiting on the "this article is live" confirm. Held because
   * starting one SAVES the form first, and on a published article that save is
   * an edit to the page the public can already read.
   */
  const [pendingGeneration, setPendingGeneration] = React.useState<{
    kind: Generation;
    options?: { brief?: string; quiet?: boolean };
  } | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  /**
   * The reading order the author arranged in the settings sheet, held until Save
   * flushes it. Null means "nobody has touched it", in which case the list shown
   * is the one the store already implies.
   */
  const [pendingOrder, setPendingOrder] = React.useState<ReadingOrderItem[] | null>(null);
  /**
   * How tall the author dragged the body editor. Held HERE rather than inside
   * the editor because the editor is keyed by article id, and the first save of
   * a new article changes that key: a height owned down there would snap back to
   * its default at the exact moment the author has written enough to have
   * resized it.
   */
  const [editorHeight, setEditorHeight] = React.useState<number | undefined>(undefined);

  /**
   * The claim request is in flight. Covers the blink between pressing Generate
   * and the service acknowledging the job — after that `generating` below is the
   * answer, and it is the service's, not this tab's.
   */
  const [claiming, setClaiming] = React.useState(false);
  /**
   * `saveDraft` is declared further down and closes over most of this component's
   * state. Reached through a ref so that starting a generation on an unsaved
   * draft can save it first without dragging that whole dependency list into
   * `runGeneration` — and without a use-before-declaration.
   */
  const saveDraftRef = React.useRef<() => Promise<string | null>>(async () => null);
  /** Which Generate button opened the brief dialog. */
  const [briefFor, setBriefFor] = React.useState<'article' | 'content' | null>(null);
  /** SEO was asked for on an article that has nothing to read yet. */
  const [seoBlocked, setSeoBlocked] = React.useState(false);
  /**
   * The failure the owner has already read.
   *
   * A failed job stays the article's newest job until another generation
   * replaces it, so the reason does not go away on its own — without this the
   * dialog would reopen on every render, and on every visit to the article.
   *
   * Keyed by the JOB, not by its message: a retry that fails the same way is a
   * second failure and has to say so, and keying on the words made it silent.
   */
  const [readJobId, setReadJobId] = React.useState<string | null>(null);
  /*
   * A failure the owner met on ARRIVAL rather than while watching.
   *
   * The dismissal above is component state, so it resets on every visit — and a
   * failed job stays the article's newest job until something replaces it, so
   * the same dialog for the same already-read failure blocked the editor every
   * time the article was opened. The first job this page sees was already
   * history when it loaded; only one that ARRIVES has news in it.
   */
  const arrivedFailed = React.useRef<string | null>(null);
  const seenAnyJob = React.useRef(false);

  if (!seenAnyJob.current && job) {
    seenAnyJob.current = true;
    if (job.status === 'failed') arrivedFailed.current = job.id;
  }
  /**
   * The body editor is writing a quiz. It owns that wait (the block glows where
   * the answer will land), but the RULE is page-wide: nothing is pressable while
   * anything is being written, and Save is out here.
   */
  const [writingQuiz, setWritingQuiz] = React.useState(false);
  /**
   * Bumped whenever a generation replaces the body. The editor takes its
   * markdown at mount and owns it after that (see `initialMarkdown`), so a body
   * written into the draft only reaches the screen by remounting it.
   */
  const [bodyVersion, setBodyVersion] = React.useState(0);

  // Adopt the loaded article into the form exactly once per article id. The
  // server-owned fields (status/updatedAt/publishedAt) are stripped: a spread
  // would smuggle them into the draft as of adoption time, and a later save
  // would write the STALE status back over a publish/unpublish done meanwhile.
  React.useEffect(() => {
    if (article && draft?.id !== article.id) {
      const { status, updatedAt, publishedAt, ...adoptable } = article;
      void status;
      void updatedAt;
      void publishedAt;
      setDraft((prev) => ({
        ...adoptable,
        /*
         * A byline is not something a generation writes — the service never sets
         * one — so a pass that produced none must not erase one the owner has.
         *
         * It matters for the blank-page path especially: that branch has the
         * service create the draft, so nothing carried the prefilled author
         * across, and the re-read afterwards would take it off the form with no
         * toast and no trace. Anything the pass DID produce still wins, because
         * only the fields it left empty are filled from here.
         */
        author: adoptable.author ?? prev?.author,
      }));
    }
  }, [article, draft?.id]);

  /**
   * The byline question is settled for this draft: either the default byline
   * has been offered (or there was none to offer) once the preferences loaded,
   * or the owner has typed one (or cleared one) themselves. The latch is what
   * makes clearing a prefilled byline stick.
   */
  const authorSettled = React.useRef(false);

  /**
   * Prefill a NEW article's empty byline: the one the owner last saved an
   * article under, else the owner's profile name (`profileByline`), so a brand
   * where nobody has saved an article yet does not start nameless.
   *
   * It cannot be part of `emptyDraft()`: preferences are fetched by the layout
   * and land after this page's first render. So the seed waits on `loaded`,
   * not on a value turning up: before the load both sources read as absent,
   * and settling then would skip the profile for good. It only ever fills a
   * blank, never replaces an author already on the draft, and an existing
   * article keeps whatever byline it was written with.
   */
  React.useEffect(() => {
    if (authorSettled.current || articleId || !prefsLoaded) return;
    authorSettled.current = true;
    const seed = rememberedAuthor ?? profileByline(ownerProfile);
    if (seed == null) return;
    setDraft((prev) =>
      prev && !prev.author ? { ...prev, author: { ...seed, links: seed.links.map((link) => ({ ...link })) } } : prev,
    );
  }, [articleId, prefsLoaded, rememberedAuthor, ownerProfile]);

  const isPublished = article?.status === 'published';
  /**
   * The footer offers Publish beside Save for a draft that has been saved: an
   * unsaved article has nothing to publish yet, and a published one offers
   * Unpublish from the kebab instead. The kebab keeps its own Publish.
   */
  const publishInBar = Boolean(draft?.id) && !isPublished;

  const setField = <K extends keyof BlogArticleDraft>(key: K, value: BlogArticleDraft[K]): void =>
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  // The slug is never user-editable: it follows the title plus a stable
  // suffix from the article id, mirroring the server's derivation (the server
  // generates the authoritative slug anyway and ignores the client's). New
  // unsaved drafts have no id yet, so the suffix appears after the first save.
  const onTitleChange = (title: string): void =>
    setDraft((prev) =>
      prev
        ? { ...prev, title, slug: slugify(title) + (prev.id ? `-${prev.id.slice(0, 8)}` : '') }
        : prev,
    );

  // The hero picker reports its own failures inline, so this rethrows the
  // service copy as an Error rather than surfacing a toast from here.
  const uploadHero = async (file: File): Promise<string> => {
    try {
      return await dispatch(blogUploadMedia(file)).unwrap();
    } catch (err) {
      throw new Error(blogSaveErrorMessage(err));
    }
  };

  /**
   * Asks the blog service for a quiz about the body as it stands right now,
   * unsaved edits included. Rejects with the service's own copy, which the
   * editor shows on the block it was writing.
   *
   * `undefined` when there is nothing to write about: a quiz about an empty
   * article would be a quiz about nothing, and offering the button anyway would
   * spend a call to find that out.
   */
  const writeQuiz = React.useCallback(
    async (current: QuizDraft): Promise<QuizDraft> => {
      // Pre-flight, fail-open (see runGeneration). The thrown copy lands on
      // the quiz block, where this control reports every other refusal too.
      if (!afford('quiz')) {
        openBuy();
        throw new Error(t('Not enough AI credits'));
      }

      try {
        const quiz = await dispatch(
          blogGenerateQuiz({
            bodyMarkdown: draft?.bodyMarkdown ?? '',
            title: draft?.title,
            language: draft?.language,
            // The question the block asks today, so a second press writes a
            // different quiz rather than the same one reworded.
            question: current.question,
          }),
        ).unwrap();

        // A successful quiz was charged: refresh the header chip.
        void dispatch(fetchOwnerWallets());

        return quiz;
      } catch (err) {
        if (blogInsufficientCredits(err)) {
          openBuy(blogNeededCredits(err));
          throw new Error(t('Not enough AI credits'));
        }

        throw new Error(t(blogSaveErrorMessage(err)));
      }
    },
    [dispatch, draft?.bodyMarkdown, draft?.title, draft?.language, t, afford, openBuy],
  );

  /**
   * Re-read the article after a generation lands.
   *
   * The service has already written the result INTO the article — that is the
   * whole shape of a job — so there is nothing here to merge. The one merge rule
   * lives server-side in `mergePartsIntoArticle`, field by field and only the
   * fields the pass produced; a second one in this file would be a competing
   * source of truth over the same data.
   *
   * It overwrites the form, and that is deliberate. Every control on this page
   * is inert while a job runs, so nothing can be typed DURING one; what this can
   * still overwrite is an edit made before a generation someone started
   * elsewhere. That is the auto-apply trade taken knowingly when this was
   * designed — and the alternative is worse, because leaving stale text on
   * screen means the next Save writes it back over the article the service just
   * finished writing.
   */
  const rereadArticle = React.useCallback(async (): Promise<void> => {
    if (!articleId) return;

    try {
      const { articles: fresh } = await dispatch(blogFetch()).unwrap();
      const updated = fresh.find((a) => a.id === articleId);
      if (!updated) return;

      const { status, updatedAt, publishedAt, ...adoptable } = updated;
      void status;
      void updatedAt;
      void publishedAt;

      setDraft((prev) => ({
        ...adoptable,
        /*
         * Neither of these is something a generation writes.
         *
         * A byline the service never sets, and a category the owner chose: the
         * merge rule the old client-side apply enforced said a category is only
         * ever FILLED IN, never changed, because it decides the public URL and a
         * writer's guess must not overrule a person's choice. That rule had no
         * home left once the merge moved server-side, so it lives here.
         */
        author: adoptable.author ?? prev?.author,
        categoryId: prev?.categoryId || adoptable.categoryId,
      }));
      // The body editor takes its markdown at mount and owns it after that, so a
      // freshly written body only reaches the screen by remounting it.
      setBodyVersion((version) => version + 1);
    } catch {
      // The generation itself landed; a failed re-read is a stale FORM, not a
      // lost article. Saying so would point at the wrong thing.
    }
  }, [articleId, dispatch]);

  /*
   * Ask the service where this article's generation got to.
   *
   * Runs whenever the article exists, not only after this tab presses Generate:
   * the state it is here to show is most often one this page did not start. It
   * is also what ends a generation on screen — the tick that reports the job
   * finished is what triggers the re-read.
   */
  React.useEffect(() => {
    if (!articleId) return undefined;

    void dispatch(blogPollArticleJobs(articleId));

    return pollWhileVisible(() => void dispatch(blogPollArticleJobs(articleId)));
  }, [dispatch, articleId]);

  /*
   * A generation ended. `done` means the article now holds the result and the
   * form is stale; `failed` wrote nothing at all, and the reason is already on
   * screen through `jobError`, so there is nothing to re-read.
   */
  /*
   * Which finished job's result the form already holds.
   *
   * This used to watch for a running→done TRANSITION, which is precisely what a
   * page cannot see while it is unmounted — a generation that ended while the
   * owner was elsewhere left the pre-generation text on screen with Save
   * enabled, over an article the service had already rewritten. Asking "have I
   * absorbed THIS job" instead is a question a freshly mounted page can answer.
   *
   * The first job this page ever sees counts as absorbed: the article was
   * fetched with it, so whatever it wrote is already in hand. Only a job that
   * lands while the owner is here has anything new to bring.
   */
  const [absorbedJobId, setAbsorbedJobId] = React.useState<string | null>(null);
  const seenAJob = React.useRef(false);

  React.useEffect(() => {
    if (!job) return;

    if (!seenAJob.current) {
      seenAJob.current = true;
      if (job.status === 'done') setAbsorbedJobId(job.id);

      return;
    }

    if (job.status !== 'done' || job.id === absorbedJobId) return;

    setAbsorbedJobId(job.id);
    void rereadArticle();
  }, [job, absorbedJobId, rereadArticle]);

  /**
   * Start one generation and return: the writing outlives this call, and the
   * poll above is what reports on it.
   *
   * Nothing is awaited here beyond the CLAIM. The old shape held the request
   * open for the length of the write, which is why leaving the page cancelled it
   * and a refresh lost it; the answer now lands in the article whether or not
   * anyone is still looking.
   */
  const startGeneration = React.useCallback(
    async (kind: Generation, options?: { brief?: string; quiet?: boolean }): Promise<void> => {
      if (!draft) return;

      /*
       * A whole-article pass on a page that has never been saved goes through
       * the route that MAKES the draft.
       *
       * This is the button that fills a blank page — the owner's brief is the
       * only thing on screen, and there may be no title at all. Saving first
       * would refuse them for a title they were never asked for. The service
       * creates the draft instead and titles it from their own words, which is
       * exactly what that route exists for.
       */
      if (!draft.id && kind === 'article') {
        setClaiming(true);
        try {
          const { article: created } = await dispatch(
            blogStartArticleGeneration({
              parts: PARTS[kind],
              brief: options?.brief,
              title: draft.title,
              bodyMarkdown: draft.bodyMarkdown,
              language: draft.language,
            }),
          ).unwrap();

          setDraft((prev) => (prev ? { ...prev, id: created.id, slug: created.slug } : prev));
          // The canonical URL without a router navigation, as `saveDraft` does
          // it: navigate() remounts the page and would drop the running job's
          // article id along with everything else on screen.
          window.history.replaceState(window.history.state, '', ROUTE.blogEdit(created.id));

          // The claim charged credits: refresh the header chip so the owner
          // watches the real balance, exactly as creatives does after enqueue.
          void dispatch(fetchOwnerWallets());

          return;
        } catch (err) {
          // Not enough credits (the service's 402): open Buy-credits primed
          // with the shortfall rather than dead-ending in a toast. The sheet
          // opens even when the 402 carried no readable amount — the refusal
          // is the signal, the number only the preselect.
          if (blogInsufficientCredits(err)) {
            openBuy(blogNeededCredits(err));
            throw new Error(t('Not enough AI credits'));
          }

          const message = t(blogSaveErrorMessage(err));
          if (!options?.quiet) show(message);

          throw new Error(message);
        } finally {
          setClaiming(false);
        }
      }

      /*
       * Otherwise save what is on screen FIRST, every time — not only when the
       * draft has never been saved.
       *
       * A job reads the article as STORED, because it outlives the page that
       * started it and cannot ask a form what was in it. So an edit left only
       * here is invisible to the writer, and then lost: the result is written
       * into the stored article, and re-reading it afterwards replaces the form
       * with a version that never had the edit. Committing first makes the
       * re-read additive — the author's words come back with the pass's on top
       * — which is the only way both survive.
       */
      const target = await saveDraftRef.current();
      /*
       * The save refused — an untitled draft. Throw rather than return, because
       * a caller that reports its own failures (the hero field does; it asks
       * `quiet`) otherwise reads a resolved promise as a generation that ran,
       * and shows nothing at all while nothing happens.
       */
      if (!target) throw new Error(t('Add a title before saving'));

      setClaiming(true);
      try {
        await dispatch(
          blogStartArticleJob({
            articleId: target,
            brief: {
              parts: PARTS[kind],
              brief: options?.brief,
              title: draft.title,
              bodyMarkdown: draft.bodyMarkdown,
              language: draft.language,
            },
          }),
        ).unwrap();

        // The claim charged credits: refresh the header chip so the owner
        // watches the real balance, exactly as creatives does after enqueue.
        void dispatch(fetchOwnerWallets());
      } catch (err) {
        // Not enough credits (the service's 402): open Buy-credits primed with
        // the shortfall rather than dead-ending in a toast — even when the 402
        // carried no readable amount.
        if (blogInsufficientCredits(err)) {
          openBuy(blogNeededCredits(err));
          throw new Error(t('Not enough AI credits'));
        }

        // Includes the refusal when someone else is already writing this
        // article — the service's own words, which say to wait for it.
        const message = t(blogSaveErrorMessage(err));
        if (!options?.quiet) show(message);
        throw new Error(message);
      } finally {
        setClaiming(false);
      }
    },
    [dispatch, draft, show, t, openBuy],
  );

  /**
   * Start a generation, asking first when the article is LIVE.
   *
   * Every generation saves the form before claiming the job, so on a published
   * article pressing Generate publishes whatever is in the form — a half-written
   * edit included. Publish, Unpublish and Delete each sit behind a confirm on
   * this page; this is the one path that changed a live article with none.
   */
  const runGeneration = React.useCallback(
    async (kind: Generation, options?: { brief?: string; quiet?: boolean }): Promise<void> => {
      // The pre-flight affordability gate, fail-open like creatives': it only
      // saves the round-trip (and, here, the save the generation starts with);
      // the service's 402 stays authoritative.
      if (!afford(kind)) {
        openBuy();
        return;
      }

      if (article?.status === 'published') {
        setPendingGeneration({ kind, options });
        setConfirm('generate');
        setConfirmOpen(true);

        // Deferred, not done. What happens next is reported by the confirm
        // path below, because a caller waiting on this promise is no longer the
        // thing on screen — the dialog is.
        return;
      }

      await startGeneration(kind, options);
    },
    [article?.status, startGeneration, afford, openBuy],
  );

  const onBriefConfirm = (brief: string): void => {
    const kind = briefFor;
    setBriefFor(null);
    if (kind) void runGeneration(kind, { brief }).catch(() => undefined);
  };

  /**
   * SEO is written FROM the article, so it needs one to exist. Refused here with
   * an explanation rather than sent off to have copy invented about nothing.
   */
  const askSeo = (): void => {
    if (!draft?.title.trim() || !draft.bodyMarkdown.trim()) {
      setSeoBlocked(true);
      return;
    }
    void runGeneration('seo').catch(() => undefined);
  };

  /**
   * Draws a hero for the article as it stands. Rejects with the service's copy,
   * which the hero field shows under itself — the same contract its upload
   * already uses.
   */
  const drawHero = React.useCallback((): Promise<void> => runGeneration('hero', { quiet: true }), [runGeneration]);

  /**
   * Anything is being written, or saved. Every control on the page goes inert:
   * a form edited under an arriving answer loses whichever of the two lands
   * second, and the author has no way of knowing which that was.
   */
  /*
   * Deliberately NOT gated on `pendingGeneration`.
   *
   * Adding it looked like the way to stop a second press landing behind the
   * live-article confirm, and it disabled the confirm's own button — the flag
   * that opens the dialog was the flag that made it unusable — while never being
   * cleared on cancel, so the whole page stayed inert for the life of the mount.
   * Two page-breaking bugs to save one wasted request: the service refuses a
   * duplicate claim on its unique index, which is exactly what that index is for.
   */
  const busy = saving || claiming || generating !== null || writingQuiz;

  /**
   * The SEO fields are being written. Separate from `busy` because a whole
   * ARTICLE also writes them, and that one is covered by the page-wide wash: two
   * washes over the same field would double the veil and halve the legibility.
   */
  const writingSeo = generating === 'seo';

  const errors = {
    title: !draft?.title.trim(),
    category: !draft?.categoryId,
  };

  const warnings = {
    description: !draft?.description.trim(),
    question: !draft?.question?.trim(),
    atAGlance: !draft?.atAGlance?.trim(),
    hero: !draft?.hero,
  };
  const hasWarnings = Object.values(warnings).some(Boolean);

  /**
   * The category's cluster in the order the public site would walk it: pillar
   * first, then sequence, then title. The article being edited is included from
   * the DRAFT rather than from the store, so a title typed a second ago and a
   * category just switched to are both already reflected.
   */
  const baseOrder = React.useMemo((): ReadingOrderItem[] => {
    if (!draft?.categoryId) return [];
    const rows = articles
      .filter((a) => a.categoryId === draft.categoryId && a.id !== draft.id)
      .map((a) => ({
        id: a.id,
        title: a.title,
        current: false,
        // Shown as it is stored: a category may hold SEVERAL "Start here"
        // guides, so this draft claiming the role does not demote anyone.
        pillar: a.pillar,
        draft: a.status !== 'published',
        sequence: a.sequence,
      }));
    rows.push({
      id: draft.id ?? '',
      title: draft.title,
      current: true,
      pillar: draft.pillar,
      draft: !isPublished,
      sequence: draft.sequence,
    });
    return rows
      .sort(
        (a, b) => Number(b.pillar) - Number(a.pillar) || a.sequence - b.sequence || a.title.localeCompare(b.title),
      )
      .map(({ sequence, ...item }) => {
        void sequence;
        return item;
      });
  }, [articles, draft, isPublished]);

  const order = pendingOrder ?? baseOrder;

  const onOrderChange = (next: ReadingOrderItem[]): void => {
    setPendingOrder(next);
    // The draft carries its own new position AND whatever the drag made of its
    // pillar flag; the other rows are flushed on Save.
    const at = next.findIndex((item) => item.current);
    if (at === -1) return;
    setDraft((prev) => (prev ? { ...prev, sequence: at + 1, pillar: next[at].pillar } : prev));
  };

  const onPillarChange = (pillar: boolean): void => {
    setField('pillar', pillar);
    // The toggle moves this article across the pillar seam, which is a different
    // place in the order. Rebuild from the ordering key rather than carry over an
    // arrangement that no longer describes where the row sits.
    setPendingOrder(null);
  };

  /**
   * Writes the place every OTHER article the author moved now holds: its sequence,
   * and whether the drag carried it across the pillar seam. The service has no
   * bulk-reorder route, so this is one full update per moved article (see
   * `blogReorderArticles`) and runs only when the author actually rearranged the
   * list. A failure is reported but does not fail the save: this article's own
   * position landed with it.
   */
  const flushOrder = async (): Promise<void> => {
    if (!pendingOrder) return;
    const entries = pendingOrder
      .map((item, index) => ({ id: item.id, sequence: index + 1, pillar: item.pillar }))
      .filter((entry) => entry.id !== '' && entry.id !== draft?.id);
    if (entries.length === 0) return;
    const result = await dispatch(blogReorderArticles(entries)).unwrap();
    if (result.error) show(t(result.error));
  };

  /**
   * The one refusal every save shares: an untitled draft. True when refused,
   * with the field highlighted and the reason shown.
   */
  const refuseUntitled = (): boolean => {
    if (!errors.title) return false;
    setShowErrors(true);
    show(t('Add a title before saving'));
    return true;
  };

  /**
   * `patch` is a field settled AFTER the form was last rendered: the category
   * the "assign a category" dialog has just chosen goes into the draft through
   * `setField` as well, but that lands on the next render, and this save is
   * going out now. Without it the save would carry the empty category the
   * dialog was opened for.
   */
  const saveDraft = async (patch?: Pick<BlogArticleDraft, 'categoryId'>): Promise<string | null> => {
    if (!draft) return null;
    if (refuseUntitled()) return null;
    const current = patch ? { ...draft, ...patch } : draft;
    // The server generates the slug from the title and ignores this value; the
    // derived slug is sent anyway (harmless) and the saved article is adopted
    // verbatim below, server slug included.
    const payload = { ...current, slug: slugify(current.slug) || slugify(current.title) };
    try {
      const saved = await dispatch(blogSaveArticle(payload)).unwrap();
      setDraft((prev) => (prev ? { ...prev, id: saved.id, slug: saved.slug } : prev));
      if (!current.id) {
        // Swap /blog/new for the canonical edit URL WITHOUT a router navigation:
        // navigate() would remount the page (PageTransition keys by pathname),
        // dropping keystrokes typed during the save and the success toast. The
        // router catches up on the next real navigation; refresh deep-links fine.
        window.history.replaceState(window.history.state, '', ROUTE.blogEdit(saved.id));
      }
      // Remember the byline for the next new article. Saving is what makes an
      // author real, so this rides the save rather than firing on every
      // keystroke, and a nameless one is no byline at all (see `authorToApi`)
      // so it is not worth keeping.
      if (draft.author?.name.trim()) {
        // Stored the way the ARTICLE stores it: `authorToApi` drops link rows
        // with no URL, so keeping a half-typed one here would both prefill the
        // next article with a dead row and never match what the server hands
        // back, re-POSTing the preference on every subsequent save.
        const remember = { ...draft.author, links: draft.author.links.filter((link) => link.url.trim() !== '') };
        if (!sameBlogAuthorPrefs(remember, rememberedAuthor)) {
          // Deliberately not awaited and deliberately not surfaced: the article
          // landed, and a convenience default failing to stick is not something
          // to interrupt the owner with.
          void dispatch(saveUserPreferences({ [PREFERENCE_KEYS.blogAuthor]: remember }));
        }
      }
      await flushOrder();
      setPendingOrder(null);
      return saved.id;
    } catch (err) {
      show(t(blogSaveErrorMessage(err)));
      return null;
    }
  };

  saveDraftRef.current = saveDraft;

  /**
   * Save, from the footer button or from the "assign a category" dialog that
   * the footer button stopped at. The title is checked first: a blank page gets
   * the title refusal alone, not a category dialog and then a toast.
   */
  const onSave = async (patch?: Pick<BlogArticleDraft, 'categoryId'>): Promise<void> => {
    if (!draft) return;
    if (refuseUntitled()) return;
    if (!(patch?.categoryId ?? draft.categoryId)) {
      setCategoryPrompt({ intent: 'save' });
      return;
    }
    const savedId = await saveDraft(patch);
    if (!savedId) return;
    if (isPublished) show(t('Changes saved'), { icon: 'check' });
    else show(t('Draft saved'), { icon: 'check' });
  };

  const askPublish = (): void => {
    if (errors.title) {
      setShowErrors(true);
      show(t('Fix the highlighted fields before publishing'));
      return;
    }
    // A missing category is the one gap the page can close on the spot, so it
    // gets a dialog rather than a red field; the confirm follows from there.
    if (errors.category) {
      setCategoryPrompt({ intent: 'publish' });
      return;
    }
    setConfirm('publish');
    setConfirmOpen(true);
  };

  /**
   * The "assign a category" dialog's answer: the category to put the article
   * in, made first if it does not exist yet, and then the action that was
   * stopped. A category the service refuses leaves the dialog open with what
   * was typed, and says why the way the list page does.
   */
  const assignCategory = async (selection: CategorySelection): Promise<void> => {
    const opened = categoryPrompt;
    if (!opened) return;
    let categoryId: string;
    if (selection.kind === 'existing') {
      categoryId = selection.id;
    } else {
      try {
        const { name, slug, description } = selection;
        const created = await dispatch(blogSaveCategory({ name, slug, description })).unwrap();
        categoryId = created.id;
      } catch (err) {
        show(t(blogSaveErrorMessage(err)));
        return;
      }
    }
    setField('categoryId', categoryId);
    // The cluster changed, so the arrangement made for the old one no longer
    // describes anything (the same as picking from the form's own Select).
    setPendingOrder(null);
    /*
     * The owner closed the dialog while the category was being made, or closed
     * it and opened another. The category exists now and is on the form, which
     * is as far as it goes: the action they cancelled stays cancelled, and a
     * dialog they are typing into stays open.
     */
    if (categoryPromptRef.current !== opened) return;
    setCategoryPrompt(null);
    if (opened.intent === 'save') {
      await onSave({ categoryId });
    } else {
      /*
       * Opened once the prompt has LEFT, not in the same render as its close.
       * A Modal hands focus back to its opener when its exit finishes; opened
       * together, the prompt's return fired a moment into the confirm and put
       * focus on the kebab behind it, where Enter opened the menu under the
       * dialog. The prompt's exit timer is registered a beat after this one
       * (in an effect, once the close has rendered) with the same duration,
       * so waiting the exit time alone would still open the confirm first:
       * the margin puts this strictly after it.
       */
      window.clearTimeout(confirmAfterPrompt.current);
      confirmAfterPrompt.current = window.setTimeout(() => {
        setConfirm('publish');
        setConfirmOpen(true);
      }, AFFILIATE_FORM_ANIM.SHEET_MS + ANIM.INTERACT_MS);
    }
  };

  /**
   * Dismiss the confirm, forgetting whatever it was asking about.
   *
   * Cancel used to clear only the dialog's visibility, leaving the deferred
   * generation behind it — which was harmless until something read that flag.
   */
  const closeConfirm = (): void => {
    setConfirmOpen(false);
    setPendingGeneration(null);
  };

  const runConfirm = async (): Promise<void> => {
    setConfirmOpen(false);
    if (!draft) return;
    try {
      if (confirm === 'publish') {
        const savedId = await saveDraft();
        if (!savedId) return;
        await dispatch(blogPublishArticle(savedId)).unwrap();
        show(t('Article published'), { icon: 'check' });
      } else if (confirm === 'unpublish' && draft.id) {
        await dispatch(blogUnpublishArticle(draft.id)).unwrap();
        show(t('Article unpublished'), { icon: 'check' });
      } else if (confirm === 'delete' && draft.id) {
        await dispatch(blogDeleteArticle(draft.id)).unwrap();
        navigate(ROUTE.blog());
      } else if (confirm === 'generate' && pendingGeneration) {
        const { kind, options } = pendingGeneration;
        setPendingGeneration(null);

        /*
         * `startGeneration` has already said what went wrong, in the service's
         * own words — "something is already being written for this article",
         * which tells the owner to wait. Letting this catch re-report it turned
         * that into "The change could not be saved", because the generic
         * unwrapper does not recognise an Error, and it fired even for the
         * callers that asked to stay quiet and show the failure themselves.
         */
        /*
         * Reported by TOAST here, whatever the original caller asked for.
         *
         * `quiet` exists so a field can show its own failure underneath itself —
         * the hero does — but that field stopped being the thing on screen the
         * moment this dialog opened, and its promise resolved when the press was
         * deferred. A toast is the only channel left, and saying nothing was the
         * alternative.
         */
        await startGeneration(kind, { ...options, quiet: false }).catch(() => undefined);
      }
    } catch (err) {
      show(t(blogSaveErrorMessage(err)));
    }
  };

  const categoryOptions = categories.map((category) => ({ value: category.id, label: category.name }));

  // The article-language picker offers what the CASINO supports, not what the
  // studio is translated into — see `languageOptions.ts` for why the two must
  // not be conflated. The tenant row carries the set; until it loads (or on an
  // older row that carries none) the historical en/mx pair stands in. The
  // article's own language is always kept in the list, so an existing
  // translation never renders blank.
  //
  // The VALUE stays exactly the code the casino (and the article) already use —
  // `details.languages` is control-plane data in backend codes, and that is what
  // the public page emits as `lang`/hreflang, so converting it would invent a
  // second vocabulary in the database. Only the LABEL is converted, via
  // `toAppLang`, because the endonym map is keyed by app codes (`br` -> `bra`
  // -> "Brasileiro"). Endonyms render RAW, never through `t()`: a language
  // picker names each option in its own tongue. See `config/langCodeMap.ts`.
  const languageOptions: SelectOption[] = articleLanguageCodes(
    tenantStatus?.languages ?? [],
    draft?.language ?? '',
  ).map((code) => ({ value: code, label: languageName(toAppLang(code)) }));

  const otherArticles = articles.filter((a) => a.id !== draft?.id);

  /**
   * The translation groups this article could join, named by whichever article
   * already carries each one.
   *
   * A group is a shared label, and every article JOINS IT FROM ITS OWN EDITOR —
   * picking a group here never writes to the other article. That is what keeps
   * the control honest: one save, one article, no surprise edit to a page the
   * owner is not looking at. The pair forms as the second one opts in, and the
   * public page emits hreflang only once there are at least two members.
   */
  const translationOptions = React.useMemo(() => {
    const seen = new Map<string, string>();
    for (const article of otherArticles) {
      if (article.translationGroupId && !seen.has(article.translationGroupId)) {
        seen.set(article.translationGroupId, article.title);
      }
    }
    // The draft's own group belongs in the list even when no OTHER article
    // carries it yet — a group started here must survive a re-render.
    if (draft?.translationGroupId && !seen.has(draft.translationGroupId)) {
      seen.set(draft.translationGroupId, t('A new group'));
    }
    return [
      ...Array.from(seen, ([value, title]) => ({ value, label: title })),
      { value: NEW_TRANSLATION_GROUP, label: t('Start a new translation group') },
    ];
  }, [otherArticles, draft?.translationGroupId, t]);

  const onTranslationGroupChange = (value: string): void =>
    setField('translationGroupId', value === NEW_TRANSLATION_GROUP ? crypto.randomUUID() : value || undefined);

  /**
   * The draft shaped as the article it would become, for the preview sheet. The
   * server-owned fields are borrowed from the saved copy where there is one, and
   * stamped with now for a draft that has never been saved, so the byline reads
   * as it will rather than as a blank.
   */
  const previewArticle = React.useMemo(
    (): BlogArticle | null =>
      draft
        ? {
            ...draft,
            id: draft.id ?? 'preview',
            status: isPublished ? 'published' : 'draft',
            updatedAt: article?.updatedAt ?? moment().toISOString(),
            publishedAt: article?.publishedAt,
            // Server-owned like the rest: kept from the saved copy, and a draft
            // that has never been saved is the owner's own.
            origin: article?.origin ?? 'studio',
          }
        : null,
    [draft, article, isPublished],
  );

  const menuItems: ActionMenuItem[] = [
    isPublished
      ? {
          label: 'Unpublish',
          icon: 'close-circle' as const,
          onSelect: () => {
            setConfirm('unpublish');
            setConfirmOpen(true);
          },
        }
      : { label: 'Publish', icon: 'check' as const, onSelect: askPublish },
  ];
  if (draft?.id && !isPublished) {
    menuItems.push({
      label: 'Delete draft',
      icon: 'trash' as const,
      destructive: true,
      onSelect: () => {
        setConfirm('delete');
        setConfirmOpen(true);
      },
    });
  }

  /*
   * What the page says when the load failed, in the SERVICE's own words.
   *
   * Same reasoning as the blog list's (`pages/blog/index.tsx`): the slice has
   * already put the service's explanation in `state.blog.error`, and rendering
   * one fixed sentence instead made a 401, a 403, a missing tenant and a 500
   * indistinguishable on screen. The copy is already a locale key, so it goes
   * through `<Text>`'s `text` prop; the fixed sentence stays as the fallback
   * for a rejection that carried none.
   */
  const loadErrorText = fetchError != null && fetchError.trim() !== '' ? fetchError : 'The blog could not be loaded.';

  /*
   * This brand's blog was never turned on, so there is no tenant to write
   * into: every save here would be refused by the service. The switch lives on
   * the blog page, so that is where this points — rather than letting an
   * author fill in a form whose first save cannot land.
   */
  if (tenantStatus?.provisioned === false) {
    return (
      <PageShell>
        <Section>
          <BackHeader title={id ? t('Edit article') : t('New article')} onBack={() => navigate(ROUTE.blog())} />
          <Panel>
            <EmptyState text="This brand's blog is not turned on yet." />
            <RetryWrap>
              <Button label={t('Go to Blog')} variant="outline" onClick={() => navigate(ROUTE.blog())} />
            </RetryWrap>
          </Panel>
        </Section>
      </PageShell>
    );
  }

  if (!draft || !previewArticle) {
    return (
      <PageShell>
        <Section>
          <BackHeader title={t('Edit article')} onBack={() => navigate(ROUTE.blog())} />
          {fetchError != null ? (
            <Panel>
              <EmptyState text={loadErrorText} />
              <RetryWrap>
                <Button label={t('Retry')} variant="outline" onClick={() => void dispatch(blogFetch())} />
              </RetryWrap>
            </Panel>
          ) : loaded && id && !article ? (
            <Panel>
              <EmptyState text="This article does not exist anymore." />
            </Panel>
          ) : (
            <LoadingStack>
              <Skeleton height={52} block />
              <Skeleton height={220} block />
              <Skeleton height={320} block />
            </LoadingStack>
          )}
        </Section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Section>
        <BackHeader
          title={id ? t('Edit article') : t('New article')}
          onBack={() => navigate(ROUTE.blog())}
          stackActionOnMobile
          action={
            <HeaderActions>
              {/* First of the three: it is the one that fills a blank page, so it
                  is what an owner opening a new article reaches for. */}
              <GenerateButton
                label={t('Generate article')}
                cost={costFor('article')}
                disabled={busy}
                onClick={() => setBriefFor('article')}
              />
              <Button
                label="Preview article"
                icon="eye"
                iconOnly
                variant="outline"
                disabled={busy}
                onClick={() => setPreviewOpen(true)}
              />
              <Button
                label="Article settings"
                icon="settings"
                iconOnly
                variant="outline"
                disabled={busy}
                onClick={() => setSettingsOpen(true)}
              />
            </HeaderActions>
          }
        />

        <Panel>
          <PanelBody>
            <PanelHead>
              <SectionTitle title={t('Article details')} />
              <StatusRow>
                {isPublished ? (
                  <Badge label={t('Published')} tone="success" />
                ) : (
                  <Badge label={t('Draft')} tone="neutral" />
                )}
                <ActionMenu items={menuItems} label="Article actions" disabled={busy} />
              </StatusRow>
            </PanelHead>

            <div>
              <Field
                label={t('Title')}
                value={draft.title}
                disabled={busy}
                onChange={onTitleChange}
                placeholder={t('Article title')}
              />
              {showErrors && errors.title ? (
                <ErrorHint text="A title is required." tag="paragraph" />
              ) : (
                // The slug is generated, never typed, so it reads as a caption of
                // the title it comes from rather than as an input of its own.
                <SlugCaption
                  text={draft.slug ? 'Public URL: /blog/category/{{slug}}' : 'The public URL follows the title.'}
                  placeholders={{ slug: draft.slug }}
                  tag="paragraph"
                />
              )}
            </div>

            <SplitRow>
              <div>
                <Select
                  label={t('Category')}
                  value={draft.categoryId}
                  disabled={busy}
                  onChange={(categoryId) => {
                    setField('categoryId', categoryId);
                    // The cluster changed, so the arrangement made for the old
                    // one no longer describes anything.
                    setPendingOrder(null);
                  }}
                  options={categoryOptions}
                  placeholder={t('Pick a category')}
                  error={showErrors && errors.category ? t('Pick a category before publishing') : undefined}
                />
              </div>
              <div>
                <Select
                  label={t('Language')}
                  value={draft.language}
                  disabled={busy}
                  onChange={(language) => setField('language', language)}
                  options={languageOptions}
                />
              </div>
            </SplitRow>

            <Select
              label={t('Translation of')}
              value={draft.translationGroupId ?? ''}
              disabled={busy}
              onChange={onTranslationGroupChange}
              options={translationOptions}
              placeholder={t('Not a translation')}
            />

            <HeroField
              hero={draft.hero}
              onChange={(hero) => setField('hero', hero)}
              onUpload={uploadHero}
              // Nothing to draw a hero FOR yet: the offer is withheld rather
              // than made and then refused.
              onGenerate={draft.title.trim() || draft.bodyMarkdown.trim() ? drawHero : undefined}
              generateCost={costFor('hero')}
              generating={generating === 'hero'}
              busy={busy}
            />
          </PanelBody>
        </Panel>

        <Panel>
          <PanelBody>
            <PanelHead>
              <SectionTitle title={t('Content')} />
              <GenerateButton
                label={t('Generate')}
                name={t('Generate the article body')}
                cost={costFor('content')}
                disabled={busy}
                onClick={() => setBriefFor('content')}
              />
            </PanelHead>
            <WashHost>
              <MarkdownEditor
                key={`${draft.id ?? 'new'}-${bodyVersion}`}
                label={t('Content')}
                initialMarkdown={draft.bodyMarkdown}
                onChange={(bodyMarkdown) => setField('bodyMarkdown', bodyMarkdown)}
                initialHeight={editorHeight}
                onHeightChange={setEditorHeight}
                // No body, nothing to write a quiz about: the Lumio controls are
                // withheld rather than offered and then refused.
                onWriteQuiz={draft.bodyMarkdown.trim() ? writeQuiz : undefined}
                quizCost={costFor('quiz')}
                onBusyChange={setWritingQuiz}
                // Its own quiz does not lock it out of itself: the toolbar is
                // where the regenerate CTA lives, and it greys those buttons
                // itself for as long as the quiz takes.
                disabled={saving || claiming || generating !== null}
              />
              {generating === 'content' ? <GeneratingWash label={t('Lumio is writing the article body')} /> : null}
            </WashHost>
          </PanelBody>
        </Panel>

        <Panel>
          <PanelBody>
            <PanelHead>
              <SectionTitle title={t('SEO')} />
              <GenerateButton
                label={t('Generate')}
                name={t('Generate the SEO fields')}
                cost={costFor('seo')}
                disabled={busy}
                onClick={askSeo}
              />
            </PanelHead>
            <WashHost>
              <Field
                label={t('Meta title')}
                value={draft.metaTitle ?? ''}
                disabled={busy}
                onChange={(metaTitle) => setField('metaTitle', metaTitle || undefined)}
                placeholder={t('Overrides the title in search results')}
              />
              {writingSeo ? <GeneratingWash label={t('Lumio is writing the SEO fields')} /> : null}
            </WashHost>
            <WashHost>
              <TextArea
                label={t('Meta description')}
                rows={2}
                value={draft.description}
                disabled={busy}
                onChange={(description) => setField('description', description)}
                hint={`${draft.description.length}/160`}
              />
              {writingSeo ? <GeneratingWash label={t('Lumio is writing the SEO fields')} /> : null}
            </WashHost>
            <WashHost>
              <Field
                label={t('Question this article answers')}
                value={draft.question ?? ''}
                disabled={busy}
                onChange={(question) => setField('question', question || undefined)}
                placeholder={t('The exact question a reader would search')}
              />
              {writingSeo ? <GeneratingWash label={t('Lumio is writing the SEO fields')} /> : null}
            </WashHost>
            <WashHost>
              <TextArea
                label={t('At a glance')}
                rows={4}
                value={draft.atAGlance ?? ''}
                disabled={busy}
                onChange={(atAGlance) => setField('atAGlance', atAGlance || undefined)}
                placeholder={t('A 60-90 word plain answer, shown in a box before the article')}
              />
              {writingSeo ? <GeneratingWash label={t('Lumio is writing the SEO fields')} /> : null}
            </WashHost>
            <StringListEditor
              label={t('Key takeaways')}
              rowLabel={t('Key takeaway')}
              addLabel={t('Add a takeaway')}
              emptyText={t('One line per point the article makes, printed in this order above the body.')}
              values={draft.keyTakeaways}
              onChange={(keyTakeaways) => setField('keyTakeaways', keyTakeaways)}
              busy={busy}
              generating={writingSeo}
            />
            {/* The byline is the one field a generator deliberately does not
                fill in, so it carries no wash and no Generate button. */}
            <AuthorField
              value={draft.author}
              onChange={(author) => {
                // Touching the byline settles it, so the default byline
                // cannot land on top of a decision just made here (typing one
                // before preferences arrive, or clearing the prefilled one).
                authorSettled.current = true;
                setField('author', author);
              }}
            />
            <FaqEditor
              values={draft.faq}
              onChange={(faq) => setField('faq', faq)}
              busy={busy}
              generating={writingSeo}
            />
            <SourcesEditor
              values={draft.sources}
              onChange={(sources) => setField('sources', sources)}
              busy={busy}
              generating={writingSeo}
            />
            <TagInput
              label={t('Tags')}
              values={draft.tags}
              onChange={(tags) => setField('tags', tags)}
              placeholder={t('Add a tag and press Space')}
              commitOnSpace
              busy={busy}
              generating={writingSeo}
            />
            <TagInput
              label={t('Entities')}
              values={draft.entities}
              onChange={(entities) => setField('entities', entities)}
              placeholder={t('Named things: providers, regulators, games')}
              commitOnSpace
              busy={busy}
              generating={writingSeo}
            />
            {otherArticles.length > 0 ? (
              <Group>
                <GroupLabel text="Related articles" tag="span" />
                <ChipRow>
                  {otherArticles.map((other) => (
                    <Chip
                      key={other.id}
                      label={other.title}
                      value={other.slug}
                      selected={draft.relatedSlugs.includes(other.slug)}
                      onClick={() =>
                        setField(
                          'relatedSlugs',
                          draft.relatedSlugs.includes(other.slug)
                            ? draft.relatedSlugs.filter((slug) => slug !== other.slug)
                            : [...draft.relatedSlugs, other.slug],
                        )
                      }
                    />
                  ))}
                </ChipRow>
              </Group>
            ) : null}
          </PanelBody>
        </Panel>

        <FooterBar>
          {/* With Publish beside it, Save steps down to the secondary style so the
              one filled button is the one that goes live. */}
          <FooterSave
            label={isPublished ? t('Save changes') : t('Save draft')}
            variant={publishInBar ? 'outline' : 'primary'}
            disabled={busy}
            onClick={() => void onSave()}
          />
          {publishInBar ? <FooterPublish label={t('Publish')} disabled={busy} onClick={askPublish} /> : null}
        </FooterBar>
      </Section>

      {/* The whole page, while a whole article is written. It is the one
          generation with no single place to point at: it replaces the title, the
          body, every SEO field and the hero at once. */}
      {generating === 'article' ? (
        <GeneratingWash variant="page" caption={t(PAGE_CAPTION)} label={t('Lumio is writing your article')} />
      ) : null}

      <BriefModal
        open={briefFor !== null}
        title={briefFor === 'content' ? t('Write the article body') : t('Write this article')}
        copy={
          briefFor === 'content'
            ? t('Lumio writes the body and leaves everything else alone: the title you chose, the SEO fields and the hero all stay as they are.')
            : t('Lumio writes the whole article: the title, the body, the SEO fields and a hero image. Anything already in the form is replaced.')
        }
        placeholder={t('What should this article cover? Say it in your own words.')}
        // The title is what the author has already decided this is about, so the
        // body dialog starts from it rather than from a blank box.
        initialBrief={briefFor === 'content' ? draft.title : ''}
        onClose={() => setBriefFor(null)}
        onGenerate={onBriefConfirm}
        cost={briefFor === 'content' ? costFor('content') : costFor('article')}
      />

      {/* A generation that failed wrote NOTHING — no title, no body, no hero —
          so the article simply did not change, and without this the owner is
          left watching a spinner stop and nothing happen. The copy is the
          service's own account of it, which is the only one that exists. */}
      <Modal
        open={jobError !== null && job !== null && job.id !== readJobId && job.id !== arrivedFailed.current}
        title={t('That generation did not finish')}
        onClose={() => setReadJobId(job?.id ?? null)}
        footer={<Button label={t('Got it')} onClick={() => setReadJobId(job?.id ?? null)} />}
      >
        <ModalCopy text={jobError ?? ''} tag="paragraph" />
        <ModalCopy text="The article was left as it was. You can try again." tag="paragraph" />
      </Modal>

      <Modal
        open={seoBlocked}
        title={t('Write the article first')}
        onClose={() => setSeoBlocked(false)}
        footer={<Button label={t('Got it')} onClick={() => setSeoBlocked(false)} />}
      >
        <ModalCopy
          text="The SEO fields are written FROM the article: the meta description, the takeaways and the FAQ all describe what the body actually says."
          tag="paragraph"
        />
        <ModalCopy text="Add a title and some body content, then come back to this." tag="paragraph" />
      </Modal>

      {settingsOpen ? (
        <ArticleSettingsSheet
          pillar={draft.pillar}
          onPillarChange={onPillarChange}
          order={order}
          onOrderChange={onOrderChange}
          onClose={() => setSettingsOpen(false)}
        />
      ) : null}

      {previewOpen ? (
        <PreviewSheet
          article={previewArticle}
          articles={articles}
          categories={categories}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}

      <CategoryPrompt
        open={categoryPrompt !== null}
        categories={categories}
        // A failed read counts as read: the picker cannot wait for a list that
        // is not coming, and creating a category is still open to the owner.
        loaded={loaded || fetchError != null}
        actionLabel={
          lastCategoryIntent.current === 'publish' ? t('Publish') : isPublished ? t('Save changes') : t('Save draft')
        }
        busy={busy}
        onClose={() => setCategoryPrompt(null)}
        onConfirm={(selection) => void assignCategory(selection)}
      />

      <Modal
        open={confirmOpen}
        title={
          confirm === 'publish'
            ? t('Publish this article?')
            : confirm === 'unpublish'
              ? t('Unpublish this article?')
              : confirm === 'generate'
                ? t('This article is live')
                : t('Delete this draft?')
        }
        onClose={() => closeConfirm()}
        footer={
          <>
            <Button label={t('Cancel')} variant="outline" onClick={() => closeConfirm()} />
            {confirm === 'publish' ? <Button label={t('Publish')} disabled={busy} onClick={() => void runConfirm()} /> : null}
            {confirm === 'unpublish' ? (
              <Button label={t('Unpublish')} disabled={busy} onClick={() => void runConfirm()} />
            ) : null}
            {confirm === 'delete' ? (
              <Button label={t('Delete draft')} disabled={busy} onClick={() => void runConfirm()} />
            ) : null}
            {confirm === 'generate' ? (
              <Button label={t('Save and generate')} disabled={busy} onClick={() => void runConfirm()} />
            ) : null}
          </>
        }
      >
        {confirm === 'publish' ? (
          <>
            <ModalCopy text="The article will be visible on your casino blog right away." tag="paragraph" />
            {hasWarnings ? (
              <>
                <ModalCopy text="Worth fixing (you can also do it after publishing):" tag="paragraph" />
                <WarnList>
                  {warnings.description ? <WarnItem text="Add a meta description for search results." tag="span" /> : null}
                  {warnings.question ? <WarnItem text="Add the question this article answers." tag="span" /> : null}
                  {warnings.atAGlance ? <WarnItem text="Add an At-a-glance summary." tag="span" /> : null}
                  {warnings.hero ? <WarnItem text="Add a hero image." tag="span" /> : null}
                </WarnList>
              </>
            ) : null}
          </>
        ) : null}
        {confirm === 'unpublish' ? (
          <ModalCopy
            text="The article will be removed from your casino blog and its public URL will stop working."
            tag="paragraph"
          />
        ) : null}
        {confirm === 'delete' ? (
          <ModalCopy text="The draft and its content are deleted. This cannot be undone." tag="paragraph" />
        ) : null}
        {confirm === 'generate' ? (
          <>
            <ModalCopy
              text="Generating saves the article first, so anything you have changed here goes live straight away."
              tag="paragraph"
            />
            <ModalCopy text="The writing itself happens afterwards and lands when it is done." tag="paragraph" />
          </>
        ) : null}
      </Modal>

      {buyOpen ? (
        <BuyCreditsSheet
          suggestedCredits={buySuggested}
          onClose={() => setBuyOpen(false)}
          onSuccess={() => show(t('Credits added to your balance.'))}
        />
      ) : null}

      <Toast toast={toast} />
    </PageShell>
  );
};

export default PageBlogEditor;
